import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const SlideContent = ({ slide }) => (
  <>
    <div className="absolute inset-0 bg-black">
      <img
        src={slide.videoUrl}
        alt={slide.artist}
        className="w-full h-full object-contain"
        style={{ objectPosition: 'center center' }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80" />
    </div>

    <div className="relative h-full flex flex-col justify-end pb-32 px-6 md:px-12">
      <div className="container mx-auto">
        <div className="max-w-4xl animate-slide-up">
          <p className="text-silver-400 text-sm md:text-base font-medium tracking-widest uppercase mb-4">
            {slide.tagline}
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-4 leading-none">
            <span className="text-white">{slide.artist}</span>
          </h1>
          <h2 className="text-3xl md:text-5xl font-light text-silver-300 mb-8">{slide.album}</h2>
          <div className="flex flex-wrap gap-4">
            <a
              href={slide.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 inline-block"
            >
              Listen Now
            </a>
          </div>
        </div>
      </div>
    </div>
  </>
);

const arrowBtnClass =
  'w-12 h-12 rounded-full border-2 border-silver-400/50 hover:border-silver-400 flex items-center justify-center text-silver-400 transition-all duration-300 hover:bg-silver-400/10';

const CarouselControls = ({ slides, currentSlide, onPrev, onNext, onSelect }) => (
  <>
    <div className="absolute bottom-8 right-6 md:right-12 flex items-center gap-4 z-10">
      <button type="button" onClick={onPrev} className={arrowBtnClass} aria-label="Previous slide" data-testid="hero-prev">
        <ChevronLeft size={24} />
      </button>
      <button type="button" onClick={onNext} className={arrowBtnClass} aria-label="Next slide" data-testid="hero-next">
        <ChevronRight size={24} />
      </button>
    </div>

    <div className="absolute bottom-8 left-6 md:left-12 flex gap-2">
      {slides.map((s, index) => (
        <button
          type="button"
          key={s.id}
          data-testid={`hero-indicator-${index}`}
          onClick={() => onSelect(index)}
          className={`h-1 transition-all duration-300 ${
            index === currentSlide ? 'w-12 bg-silver-400' : 'w-6 bg-silver-400/30 hover:bg-silver-400/50'
          }`}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  </>
);

const HeroSection = () => {
  const { heroSlides } = useContent();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || heroSlides.length === 0) return undefined;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides.length]);

  const goTo = (index) => {
    setCurrentSlide((index + heroSlides.length) % heroSlides.length);
    setIsAutoPlaying(false);
  };

  const slide = heroSlides[currentSlide];
  if (!slide) return null;

  return (
    <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      <SlideContent slide={slide} />
      <CarouselControls
        slides={heroSlides}
        currentSlide={currentSlide}
        onPrev={() => goTo(currentSlide - 1)}
        onNext={() => goTo(currentSlide + 1)}
        onSelect={goTo}
      />
    </section>
  );
};

export default HeroSection;
